import React, { useEffect } from "react";
import { MapContainer, TileLayer, Circle, Polygon, Marker, Popup, Tooltip } from "react-leaflet";
import { colorCodeMarker, colorCodeMarkerHighlight, convertToLocal, getTime, getDate, colorCodeColor } from "../../../common/utils/functions";
import { droneIcon, greyIcon } from "../../../common/utils/markers";
import { useTranslation } from "react-i18next";
import * as turf from "@turf/turf";

export const AtsMap = ({
  active,
  user,
  zones,
  previewZone,
  toggleZones,
  highlightActive,
  highlightUpcoming,
  highlightEarlier,
  highlightErased,
  ctrAirspaces,
  restrictedAreasAirspaces,
  bufferZonesAirspaces,
  futureObjectsAirspaces,
  trafficInformationZonesAirspaces,
  airTrafficZonesAirspaces,
  restrictedAreasTemporaryAirspaces,
  uasZonesAirspaces,
  drones,
}) => {
  const { t } = useTranslation();

  const toLatLng = (coordinate) => {
    if (!coordinate) {
      return null;
    }

    if (Array.isArray(coordinate) && coordinate.length >= 2) {
      const first = Number(coordinate[0]);
      const second = Number(coordinate[1]);

      if (Number.isNaN(first) || Number.isNaN(second)) {
        return null;
      }

      if (Math.abs(first) <= 90 && Math.abs(second) <= 180) {
        return [first, second];
      }

      return [second, first];
    }

    const latitude = Number(coordinate.latitude ?? coordinate.lat);
    const longitude = Number(coordinate.longitude ?? coordinate.lng);

    if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
      return null;
    }

    return [latitude, longitude];
  };

  const toLeafletCoordinates = (rawCoordinates) => {
    if (!Array.isArray(rawCoordinates) || rawCoordinates.length === 0) {
      return [];
    }

    if (Array.isArray(rawCoordinates[0]) && typeof rawCoordinates[0][0] === "number") {
      return rawCoordinates.map(toLatLng).filter(Boolean);
    }

    if (Array.isArray(rawCoordinates[0])) {
      return rawCoordinates.map((part) => toLeafletCoordinates(part)).filter((part) => Array.isArray(part) && part.length > 0);
    }

    return [];
  };

  const parseJsonIfString = (value) => {
    if (typeof value !== "string") {
      return value;
    }

    try {
      return JSON.parse(value);
    } catch (err) {
      return value;
    }
  };

  const getAirspaceLabel = (airspace) => {
    return airspace?.name || airspace?.type || airspace?.airspaceTypeGeneralInfo || airspace?.externalId || "Airspace";
  };

  const extractPolygonPositions = (airspace) => {
    const objectCoordinates = parseJsonIfString(airspace?.coordinates);
    if (Array.isArray(objectCoordinates) && objectCoordinates.length > 0 && !Array.isArray(objectCoordinates[0])) {
      const positions = objectCoordinates.map(toLatLng).filter(Boolean);
      return positions.length ? [positions] : [];
    }

    const geometry = parseJsonIfString(airspace?.geometry);
    const geometryCoordinates = parseJsonIfString(geometry?.coordinates);
    const directCoordinates = parseJsonIfString(airspace?.coordinates);
    const candidateCoordinates = geometryCoordinates || directCoordinates;

    if (!candidateCoordinates) {
      return [];
    }

    if (geometry?.type === "Polygon") {
      const rings = toLeafletCoordinates(candidateCoordinates);
      if (rings.length === 0) {
        return [];
      }
      if (Array.isArray(rings[0]) && Array.isArray(rings[0][0])) {
        return [rings[0]];
      }
      return [rings];
    }

    if (geometry?.type === "MultiPolygon") {
      return toLeafletCoordinates(candidateCoordinates);
    }

    if (Array.isArray(candidateCoordinates) && candidateCoordinates.length > 0) {
      const converted = toLeafletCoordinates(candidateCoordinates);
      if (converted.length === 0) {
        return [];
      }

      if (Array.isArray(converted[0]) && typeof converted[0][0] === "number") {
        return [converted];
      }

      if (Array.isArray(converted[0]) && Array.isArray(converted[0][0]) && typeof converted[0][0][0] === "number") {
        return [converted[0]];
      }

      return converted;
    }

    return [];
  };

  const extractCircle = (airspace) => {
    const latitude = Number(airspace?.latitude ?? airspace?.lat ?? airspace?.centerLatitude);
    const longitude = Number(airspace?.longitude ?? airspace?.lng ?? airspace?.centerLongitude);
    const radius = Number(airspace?.radius ?? airspace?.radiusMeters ?? airspace?.radiusInMeters ?? airspace?.radius_in_meters);

    if (Number.isNaN(latitude) || Number.isNaN(longitude) || Number.isNaN(radius) || radius <= 0) {
      return null;
    }

    return {
      center: [latitude, longitude],
      radius: radius,
    };
  };

  const renderAirspaceLayer = (items, layerKeyPrefix, color, fillOpacity = 0.2, opacity = 0) => {
    return items?.map((airspace, index) => {
      const polygons = extractPolygonPositions(airspace);
      const circle = extractCircle(airspace);
      const label = getAirspaceLabel(airspace);
      const id = airspace?.id || airspace?.lfv_id || `${layerKeyPrefix}-${index}`;

      return (
        <React.Fragment key={`${layerKeyPrefix}-${id}`}>
          {polygons.map((positions, polygonIndex) => (
            <Polygon key={`${layerKeyPrefix}-${id}-polygon-${polygonIndex}`} pathOptions={{ color }} positions={positions} opacity={opacity} fillOpacity={fillOpacity}>
              <Tooltip>{label}</Tooltip>
            </Polygon>
          ))}
          {!polygons.length && circle && (
            <Circle key={`${layerKeyPrefix}-${id}-circle`} center={circle.center} radius={circle.radius} fillColor={color} opacity={opacity} fillOpacity={fillOpacity}>
              <Tooltip>{label}</Tooltip>
            </Circle>
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <MapContainer id="map" center={user && user.atsLat && user.atsLng ? [user.atsLat, user.atsLng] : [62.38583179, 16.321998712]} zoom={9} scrollWheelZoom={true}>
      <TileLayer noWrap={true} zIndex={-1} attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.osm.org/{z}/{x}/{y}.png" />
      {active?.map((request) => {
        return (
          <React.Fragment key={request.id}>
            {request.geoPolygon && request.geoPolygon.length > 0 && (
              <Polygon
                key={request.id}
                pathOptions={{
                  color: "#00008b",
                  fillColor: colorCodeColor(request.positionStatus),
                }}
                positions={request.geoPolygon}
                opacity={0.5}
                fillOpacity={0.2}
              >
                <Marker icon={colorCodeMarker(request.positionStatus)} position={[request.lat, request.lng]}>
                  <Popup>
                    <b>
                      {t("id")}: {request.id} <br /> {t("pilot")}: {request.displayName}
                    </b>
                  </Popup>
                </Marker>
                {request.geoPolygonBuffer && (
                  <Polygon
                    pathOptions={{
                      color: "#00008b",
                      fillColor: colorCodeColor(request.positionStatus),
                    }}
                    positions={request.geoPolygonBuffer}
                    opacity={0.5}
                    fillOpacity={0.2}
                  ></Polygon>
                )}
              </Polygon>
            )}
          </React.Fragment>
        );
      })}
      {drones?.map((drone) => {
        return (
          <Circle key={drone.id} center={[drone.lat, drone.lng]} radius={drone.radius_in_meters} fillColor="#00008b" fillOpacity={0.1} color="#00008b" opacity={0.2}>
            <Marker icon={droneIcon} position={[drone.lat, drone.lng]}>
              <Popup>
                <p>
                  <strong>{drone.height_above_GND_in_meters}m</strong> {t("above_ground")}
                  <br />
                  {t("flight_start")} {getTime(convertToLocal(drone.planned_start_unix))} ({getDate(convertToLocal(drone.planned_start_unix))}
                  )
                  <br />
                  <strong>
                    {t("flight_end")} {getTime(convertToLocal(drone.planned_stop_unix))}
                  </strong>{" "}
                  ({getDate(convertToLocal(drone.planned_stop_unix))})
                  <br />
                  {/* {drone.is_police ? <em>{t("another_drone_is_at_location")}</em> : <em>{t("another_drone_is_at_location")}</em>} */}
                </p>
              </Popup>
            </Marker>
          </Circle>
        );
      })}
      {highlightActive && (
        <>
          {highlightActive.geoPolygon && highlightActive.geoPolygon.length > 0 && (
            <Polygon
              key={highlightActive.id}
              pathOptions={{
                color: colorCodeColor(highlightActive.positionStatus),
              }}
              positions={highlightActive.geoPolygon}
              opacity={0.2}
              fillOpacity={0.2}
            >
              <Marker icon={colorCodeMarkerHighlight(highlightActive.positionStatus)} position={[highlightActive.lat, highlightActive.lng]}>
                <Popup>
                  <b>
                    {t("id")}: {highlightActive.id} <br /> {t("pilot")}: {highlightActive.displayName}
                  </b>
                </Popup>
              </Marker>
            </Polygon>
          )}
          {highlightActive.geoPolygonBuffer && (
            <Polygon
              pathOptions={{
                color: colorCodeColor(highlightActive.positionStatus),
              }}
              positions={highlightActive.geoPolygonBuffer}
              opacity={0.2}
              fillOpacity={0.2}
            ></Polygon>
          )}
        </>
      )}
      {highlightUpcoming && (
        <>
          {highlightUpcoming.geoPolygon && highlightUpcoming.geoPolygon.length > 0 && (
            <Polygon key={highlightUpcoming.id} pathOptions={{ color: "#00008b" }} positions={highlightUpcoming.geoPolygon} opacity={0.2} fillOpacity={0.2}>
              <Marker icon={greyIcon} position={[highlightUpcoming.lat, highlightUpcoming.lng]}>
                <Popup>
                  <b>
                    {t("id")}: {highlightUpcoming.id} <br /> {t("pilot")}: {highlightUpcoming.displayName}
                  </b>
                </Popup>
              </Marker>
            </Polygon>
          )}
          {highlightUpcoming.geoPolygonBuffer && <Polygon pathOptions={{ color: "#00008b" }} positions={highlightUpcoming.geoPolygonBuffer} opacity={0.2} fillOpacity={0.2}></Polygon>}
        </>
      )}
      {highlightEarlier && (
        <>
          {highlightEarlier.geoPolygon && highlightEarlier.geoPolygon.length > 0 && (
            <Polygon key={highlightEarlier.id} pathOptions={{ color: "#00008b" }} positions={highlightEarlier.geoPolygon} opacity={0.2} fillOpacity={0.2}>
              <Marker icon={greyIcon} position={[highlightEarlier.lat, highlightEarlier.lng]}>
                <Popup>
                  <b>
                    {t("id")}: {highlightEarlier.id} <br /> {t("pilot")}: {highlightEarlier.displayName}
                  </b>
                </Popup>
              </Marker>
            </Polygon>
          )}
          {highlightEarlier.geoPolygonBuffer && <Polygon pathOptions={{ color: "#00008b" }} positions={highlightEarlier.geoPolygonBuffer} opacity={0.2} fillOpacity={0.2}></Polygon>}
        </>
      )}

      {highlightErased && (
        <>
          {highlightErased.geoPolygon && highlightErased.geoPolygon.length > 0 && (
            <Polygon key={highlightErased.id} pathOptions={{ color: "#00008b" }} positions={highlightErased.geoPolygon} opacity={0.2} fillOpacity={0.2}>
              <Marker icon={greyIcon} position={[highlightErased.lat, highlightErased.lng]}>
                <Popup>
                  <b>
                    {t("id")}: {highlightErased.id} <br /> {t("pilot")}: {highlightErased.displayName}
                  </b>
                </Popup>
              </Marker>
            </Polygon>
          )}
          {highlightErased.geoPolygonBuffer && <Polygon pathOptions={{ color: "#00008b" }} positions={highlightErased.geoPolygonBuffer} opacity={0.2} fillOpacity={0.2}></Polygon>}
        </>
      )}
      {toggleZones && (
        <div>
          {renderAirspaceLayer(restrictedAreasAirspaces, "restricted-areas", "red", 0.2, 0)}
          {renderAirspaceLayer(bufferZonesAirspaces, "buffer-zones", "red", 0.2, 0)}
          {renderAirspaceLayer(futureObjectsAirspaces, "future-objects", "red", 0.2, 0)}
          {renderAirspaceLayer(trafficInformationZonesAirspaces, "traffic-information-zones", "red", 0, 1)}
          {renderAirspaceLayer(airTrafficZonesAirspaces, "air-traffic-zones", "red", 0.2, 0)}
          {renderAirspaceLayer(restrictedAreasTemporaryAirspaces, "restricted-areas-temporary", "red", 0.2, 0)}
          {renderAirspaceLayer(uasZonesAirspaces, "uas-zones", "red", 0.2, 0)}
        </div>
      )}
      {renderAirspaceLayer(ctrAirspaces, "ctr", "red", 0, 1)}
      {zones && (
        <div>
          {zones.map((zone) => {
            return <Polygon key={zone.id} pathOptions={{ color: "#008000" }} positions={zone.area} />;
          })}
        </div>
      )}
      {previewZone && <Polygon pathOptions={{ color: "#808080" }} positions={previewZone} />}
    </MapContainer>
  );
};
